import {
  useEffect,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  useAnalytics,
  useApi,
  type BackstageIdentityResponse,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import { UserIdentity } from '@backstage/core-components';
import { Alert, Button, Card, CardBody, Text } from '@backstage/ui';
import { waadooIcons, autheliaAuthApiRef } from '@internal/plugin-waadoo-core';
import { useThemeVariant } from '../theme/useThemeVariant';
import { DEFAULT_RATE_LIMIT_MS, refreshLatch } from './refreshLatch';
import {
  bannerFor,
  errorDetail,
  formatClock,
  formatWait,
  returnPathLabel,
  type SignInBanner,
} from './signInState';
import { SignInHelp } from './SignInHelp';
import logoOnLight from './waadoo-logo.png';
import logoOnDark from './waadoo-logo.dark.png';

/** Authelia's refusal of a rate-limited code exchange, in `?error=`. */
const RATE_LIMITED_ERROR = /temporarily_unavailable|too many requests/i;

const Login = waadooIcons.login;
const Loading = waadooIcons.loading;
const ReturnTo = waadooIcons.returnTo;
const Expand = waadooIcons.expand;

/** What the card says, by why it is shown. */
const VIEWS = {
  signIn: {
    title: 'Connexion',
    text: 'Connectez-vous avec votre compte Waadoo.',
    button: 'Se connecter avec Waadoo',
  },
  expired: {
    title: 'Reprendre votre session',
    text: 'Un clic suffit si vous êtes encore connecté à Waadoo.',
    button: 'Se reconnecter',
  },
  signedOut: {
    title: 'À bientôt',
    text: 'Vous pouvez fermer cet onglet.',
    button: 'Se reconnecter',
  },
} as const;

/**
 * A live region that is inserted empty and filled one commit later: words
 * already there when a region enters the page are often not read out. Mounted
 * with the card, so its first words — the banner, the wait — are announced.
 */
function LiveRegion(props: ComponentPropsWithoutRef<'div'>) {
  const { children, ...rest } = props;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return <div {...rest}>{mounted ? children : null}</div>;
}

/**
 * The current time, ticking once a second until `until` — on the second
 * boundaries of `until`, so that the countdown reaches 0:00 exactly when the
 * button comes back — and still otherwise.
 */
function useNowUntil(until: number | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === undefined) {
      return undefined;
    }
    let timer: number | undefined;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      const left = until - current;
      if (left > 0) {
        timer = window.setTimeout(tick, left % 1000 || 1000);
      }
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [until]);
  return now;
}

/**
 * The sign-in page of deployed environments: Authelia only.
 *
 * Its sign-in logic is a copy of core-components' `SingleSignInPage`
 * (@backstage/core-components 0.18.14, layout/SignInPage/SignInPage.esm.js),
 * the component `<SignInPage provider={...} />` rendered here before; only the
 * markup differs. Kept step for step, including what looks odd, so that
 * signing in behaves exactly as it did:
 *
 * - on mount, a silent check (`getBackstageIdentity({ optional: true })`),
 *   which is also how the return from Authelia's redirect flow is picked up:
 *   the auth backend has set its refresh cookie by then;
 * - the button asks for `instantPopup`, which DefaultAuthConnector turns into
 *   a full-page redirect when `enableExperimentalRedirectFlow` is set in
 *   app-config, a popup otherwise — the choice is the auth API's, not ours;
 * - a failed redirect comes back with `?error=<message>`: the message is
 *   shown. One deliberate difference: the parameter is then removed from the
 *   URL. Upstream keeps it, and its `!errorParam` guard — there to stop the
 *   `auto` mode, unused here, from redirecting in a loop — leaves the button
 *   inert while it is there. Removing it keeps the guard and the button
 *   works; a later redirect does not carry the stale error either;
 * - a popup closed by the user (`PopupRejectedError`) shows no error.
 *
 * Added (incident 2026-10-17): why no session was found, when the auth API
 * held its refreshes back (`refreshLatch.ts`): Authelia limits the token
 * endpoint, refused the session, or the user signed out. The silent check
 * then sent no request at all; only the button resumes them. A redirect that
 * comes back with Authelia's `temporarily_unavailable` is shown as a held
 * refresh rather than the raw OAuth error, for `DEFAULT_RATE_LIMIT_MS`.
 *
 * Layout « L1 » (2026-10-18): the reason as a banner at the top of the card,
 * a title and a line that depend on it, the button — held, with a countdown,
 * while Authelia limits sign-ins — and, on a deep link, the page the user
 * will come back to. That return is the auth API's own: the redirect flow
 * sends `window.location.href` as `redirectUrl`, which the auth backend
 * redirects to after Authelia (`waadooAuthConnector.ts`, `executeRedirect`);
 * the popup flow never leaves the page, and the app renders the current URL
 * once signed in.
 */
export function WaadooSignInPage(props: SignInPageProps) {
  const { onSignInSuccess } = props;
  const authApi = useApi(autheliaAuthApiRef);
  const analytics = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const errorParam = searchParams.get('error');
  const [error, setError] = useState<Error>();
  const [banner, setBanner] = useState<SignInBanner>();
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const logo = useThemeVariant(logoOnLight, logoOnDark);

  const limitedUntil =
    banner?.kind === 'rateLimited' ? banner.until : undefined;
  const now = useNowUntil(limitedUntil);
  const secondsLeft =
    limitedUntil === undefined
      ? 0
      : Math.max(0, Math.ceil((limitedUntil - now) / 1000));
  // Announced once when the wait starts, not on every tick.
  const startWait = useMemo(
    () =>
      limitedUntil === undefined
        ? 0
        : Math.max(0, Math.ceil((limitedUntil - Date.now()) / 1000)),
    [limitedUntil],
  );

  // Never rejects: every failure, onSignInSuccess's included, ends in the
  // catch below, as in the upstream component.
  const login = async (options: {
    checkExisting?: boolean;
    showPopup?: boolean;
  }) => {
    try {
      let identityResponse: BackstageIdentityResponse | undefined;
      if (options.checkExisting) {
        identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });
      }
      if (!identityResponse && options.showPopup && !errorParam) {
        setShowLoginPage(true);
        identityResponse = await authApi.getBackstageIdentity({
          instantPopup: true,
        });
        if (!identityResponse) {
          throw new Error(
            "Le fournisseur Compte Waadoo n'est pas configuré pour la connexion.",
          );
        }
      }
      if (!identityResponse) {
        if (options.checkExisting) {
          setBanner(current => bannerFor(refreshLatch.read()) ?? current);
        }
        setShowLoginPage(true);
        setIsPending(false);
        return;
      }
      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
      analytics.captureEvent('signIn', 'success');
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setShowLoginPage(true);
      setIsPending(false);
    }
  };

  // Mount only, like upstream's useMountEffect: `login` and `errorParam` are
  // read once, when the page opens.
  useEffect(() => {
    if (errorParam && RATE_LIMITED_ERROR.test(errorParam)) {
      // Authelia says no more than « too many »: wait as long as the auth
      // API does for a 503 without Retry-After. Only shown — the latch is the
      // auth API's, and the silent check below runs as it always did.
      setBanner({
        kind: 'rateLimited',
        until: Date.now() + DEFAULT_RATE_LIMIT_MS,
      });
    } else if (errorParam) {
      setError(new Error(errorParam));
    }
    if (errorParam) {
      // Through the router, not history.replaceState: its location, which
      // `errorParam` is read from, would otherwise stay stale.
      setSearchParams(
        params => {
          params.delete('error');
          return params;
        },
        { replace: true },
      );
    }
    void login({ checkExisting: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showLoginPage) {
    return (
      <div className="waadoo-signin" role="status">
        <Loading
          className="waadoo-signin__spinner"
          aria-hidden
          focusable="false"
        />
        <span className="waadoo-visually-hidden">
          Vérification de la session…
        </span>
      </div>
    );
  }

  const shownError =
    error && error.name !== 'PopupRejectedError' ? error : undefined;
  const limited = secondsLeft > 0;
  // The latch's own words only when nothing more pressing is shown.
  let view: (typeof VIEWS)[keyof typeof VIEWS] = VIEWS.signIn;
  if (!limited && !shownError && banner?.kind === 'expired') {
    view = VIEWS.expired;
  } else if (!limited && !shownError && banner?.kind === 'signedOut') {
    view = VIEWS.signedOut;
  }
  const returnLabel = returnPathLabel(pathname);

  // Info, warning, success: in a status region that stays; a failure: its
  // own alert, which is read out on insertion.
  let statusBanner = null;
  let errorBanner = null;
  if (limited) {
    statusBanner = (
      <Alert
        className="waadoo-signin__banner"
        status="warning"
        icon
        title="Connexion momentanément indisponible"
        description="Trop de connexions en peu de temps. Le bouton se réactive automatiquement."
      />
    );
  } else if (shownError) {
    errorBanner = (
      <Alert
        className="waadoo-signin__banner"
        status="danger"
        icon
        role="alert"
        title="Connexion impossible pour le moment"
        description={
          <>
            Le service de connexion ne répond pas. Réessayez dans un instant.
            <details className="waadoo-signin__details">
              <summary>
                <Expand
                  className="waadoo-signin__details-caret"
                  aria-hidden
                  focusable="false"
                />
                Détails techniques
              </summary>
              <code>{errorDetail(shownError.message)}</code>
            </details>
          </>
        }
      />
    );
  } else if (banner?.kind === 'expired') {
    statusBanner = (
      <Alert
        className="waadoo-signin__banner"
        status="info"
        icon
        title="Votre session a expiré"
        description="Par sécurité, elle se ferme après une période d’inactivité. Reconnectez-vous pour reprendre."
      />
    );
  } else if (banner?.kind === 'signedOut') {
    statusBanner = (
      <Alert
        className="waadoo-signin__banner"
        status="success"
        icon
        title="Vous êtes déconnecté"
        description="Votre session Waadoo est fermée sur ce navigateur."
      />
    );
  }

  let announcement = '';
  if (limited) {
    announcement = `Connexion possible dans ${formatWait(startWait)}.`;
  } else if (banner?.kind === 'rateLimited') {
    announcement = 'La connexion est de nouveau possible.';
  }

  return (
    <main className="waadoo-signin">
      <div className="waadoo-signin__content">
        <div className="waadoo-signin__brand">
          <img
            className="waadoo-signin__logo"
            src={logo}
            alt=""
            width={96}
            height={96}
          />
          <Text as="h1" variant="title-large" weight="bold">
            Waadoo
          </Text>
          <Text as="p" variant="body-medium" color="secondary">
            Plateforme d’applications
          </Text>
        </div>
        <Card className="waadoo-signin__card">
          <CardBody className="waadoo-signin__body">
            {errorBanner}
            <LiveRegion role="status" className="waadoo-signin__live">
              {statusBanner}
            </LiveRegion>
            <div className="waadoo-signin__heading">
              <Text as="h2" variant="title-small" weight="bold">
                {view.title}
              </Text>
              {limited ? (
                <Text as="p" variant="body-medium" color="secondary">
                  Réessayez dans <strong>{formatWait(secondsLeft)}</strong>.
                </Text>
              ) : (
                <Text as="p" variant="body-medium" color="secondary">
                  {view.text}
                </Text>
              )}
            </div>
            <Button
              className="waadoo-signin__button"
              variant="primary"
              iconStart={<Login aria-hidden focusable="false" />}
              isPending={isPending}
              isDisabled={limited}
              onPress={() => {
                setBanner(undefined);
                setIsPending(true);
                void login({ showPopup: true });
              }}
            >
              {limited
                ? `Disponible dans ${formatClock(secondsLeft)}`
                : view.button}
            </Button>
            <LiveRegion
              className="waadoo-visually-hidden waadoo-signin__announcer"
              aria-live="polite"
            >
              {announcement}
            </LiveRegion>
            {returnLabel && (
              <Text
                as="p"
                variant="body-small"
                color="secondary"
                className="waadoo-signin__return"
              >
                <ReturnTo
                  className="waadoo-signin__return-icon"
                  aria-hidden
                  focusable="false"
                />
                <span>
                  Après connexion, vous reviendrez sur{' '}
                  <strong className="waadoo-signin__return-target">
                    {returnLabel}
                  </strong>
                </span>
              </Text>
            )}
            <div className="waadoo-signin__footer">
              <Text as="p" variant="body-small" color="secondary">
                Accès réservé aux équipes Waadoo et à leurs clients.
              </Text>
              <SignInHelp />
            </div>
          </CardBody>
        </Card>
      </div>
    </main>
  );
}
